'use client';

import type { ComponentType } from 'react';
import { Baby, Calendar, Check, Clock, Heart, MapPin, User, Users } from 'lucide-react';
import type { JoinField, LocationCongregation, LocationGroup } from '@/lib/types';
import { CampusDropdown } from './CampusDropdown';

/**
 * One question of the "Join a Group" form. Branches on our `component`
 * first (from Custom_Field_Configuration) and MP's field type second, so a
 * field nobody configured still renders the way MP's own widget would.
 */

const ICONS: Record<string, ComponentType<{ className?: string; strokeWidth?: number }>> = {
  calendar: Calendar,
  clock: Clock,
  users: Users,
  user: User,
  baby: Baby,
  heart: Heart,
  'map-pin': MapPin,
};

const LABEL = 'text-xs font-bold tracking-widest text-gray-500 uppercase';
const HELP = 'text-xs text-gray-500';
const FIELD =
  'w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-[#1c2b39] placeholder:text-gray-400 focus:border-[#299c8f] focus:ring-2 focus:ring-[#299c8f]/30 focus:outline-none';
const CHIP =
  'inline-flex items-center gap-2 rounded-full py-1.5 pr-4 pl-1.5 text-sm font-semibold transition-all hover:scale-105 hover:shadow-md active:scale-95';
const CHIP_IDLE = 'bg-white text-[#1c2b39] ring-1 ring-gray-200';
const CHIP_ON = 'bg-[#1c2b39] text-white shadow-md';

function Head({ field, note }: { field: JoinField; note?: string }) {
  const help = field.config?.helperText ?? note;
  return (
    <div className="flex flex-col gap-0.5">
      <span className={LABEL}>
        {field.config?.title ?? field.label}
        {field.required && <span className="ml-1 text-[#299c8f]">*</span>}
      </span>
      {help && <span className={HELP}>{help}</span>}
    </div>
  );
}

function Pill({
  on,
  icon,
  label,
  onClick,
}: {
  on: boolean;
  icon?: string;
  label: string;
  onClick: () => void;
}) {
  const Icon = icon ? ICONS[icon] : undefined;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={`${CHIP} ${on ? CHIP_ON : CHIP_IDLE}`}
    >
      <span
        className={`flex h-7 w-7 items-center justify-center rounded-full ${on ? 'bg-white/15 text-white' : 'bg-gray-100 text-gray-500'}`}
      >
        {on ? (
          <Check className="h-4 w-4" strokeWidth={3} />
        ) : Icon ? (
          <Icon className="h-4 w-4" strokeWidth={2.25} />
        ) : (
          <span className="h-2 w-2 rounded-full bg-current opacity-40" />
        )}
      </span>
      {label}
    </button>
  );
}

interface JoinFieldProps {
  field: JoinField;
  /** The field's own children (a multiselect's choices) */
  choices: JoinField[];
  value: string;
  onChange: (value: string) => void;
  childValues: Record<number, string>;
  onChildChange: (id: number, value: string) => void;
  /** For the campus picker */
  locations: LocationGroup[];
  campusId: number | null;
  onCampus: (cong: LocationCongregation | null, svgUrl: string | null) => void;
}

export function JoinFieldControl({
  field,
  choices,
  value,
  onChange,
  childValues,
  onChildChange,
  locations,
  campusId,
  onCampus,
}: JoinFieldProps) {
  const component = field.config?.component;
  const iconFor = (option: string) => field.config?.optionIcons?.[option] ?? field.config?.icon;

  if (component === 'CampusPicker') {
    return (
      <div className="flex flex-col gap-3">
        <Head field={field} />
        <CampusDropdown locations={locations} selectedId={campusId} onSelect={onCampus} />
      </div>
    );
  }

  // MP's multiselect: an Instructions parent whose children are the choices.
  if (field.kind === 'instructions' && field.isMultiselect) {
    return (
      <div className="flex flex-col gap-3">
        <Head field={field} />
        <div className="flex flex-wrap gap-2">
          {choices.map((c) => {
            const on = (childValues[c.id] ?? '') !== '';
            return (
              <Pill
                key={c.id}
                on={on}
                icon={iconFor(c.label)}
                label={c.label}
                onClick={() => onChildChange(c.id, on ? '' : 'Yes')}
              />
            );
          })}
        </div>
      </div>
    );
  }

  if (field.kind === 'instructions') {
    return <p className="text-sm leading-relaxed text-gray-600">{field.label}</p>;
  }

  if (field.kind === 'file') return null; // not offered here

  if (field.kind === 'checkbox') {
    const on = value !== '';
    return (
      <div className="flex flex-col gap-3">
        <Head field={field} />
        <div>
          <Pill
            on={on}
            icon={iconFor(field.label)}
            label={on ? 'Yes' : 'No'}
            onClick={() => onChange(on ? '' : 'Yes')}
          />
        </div>
      </div>
    );
  }

  // A short list of choices reads better as pills than a dropdown.
  if (
    component === 'Pills' ||
    field.kind === 'radio' ||
    (field.kind === 'select' && field.options.length <= 6)
  ) {
    return (
      <div className="flex flex-col gap-3">
        <Head field={field} />
        <div className="flex flex-wrap gap-2">
          {field.options.map((o) => (
            <Pill
              key={o}
              on={value === o}
              icon={iconFor(o)}
              label={o}
              onClick={() => onChange(value === o ? '' : o)}
            />
          ))}
        </div>
      </div>
    );
  }

  if (field.kind === 'select') {
    return (
      <div className="flex flex-col gap-3">
        <Head field={field} />
        <select value={value} onChange={(e) => onChange(e.target.value)} className={FIELD}>
          <option value="">Choose…</option>
          {field.options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (component === 'LongText' || field.kind === 'memo') {
    return (
      <label className="flex flex-col gap-3">
        <Head field={field} />
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={4}
          maxLength={2000}
          className={FIELD}
        />
      </label>
    );
  }

  const type = field.kind === 'date' ? 'date' : field.kind === 'phone' ? 'tel' : 'text';
  return (
    <label className="flex flex-col gap-3">
      <Head field={field} />
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={500}
        autoComplete={type === 'tel' ? 'tel' : 'off'}
        className={`${FIELD} rounded-full`}
      />
    </label>
  );
}
